import PropTypes from 'prop-types';
import React, { useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import AccessibleTextField from 'app/components/deprecated/shared/AccessibleTextField';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { fieldErrorsShape } from '@bibliocommons/bc-prop-types';
import { TextButton } from '@bibliocommons/deprecated-base-buttons';
import { Cross as CrossIcon } from '@bibliocommons/deprecated-base-icons';

import './ClearableTextField.scss';

function ClearableTextField({
  id,
  value,
  type,
  labelledById,
  describedById,
  fieldErrors,
  errorKey,
  handleChange,
  handleClear,
  placeholder,
  maxLength,
  focus,
  inputTestId,
  info,
  className,
  characterCountSrOnly,
  hideClearButton,
  clearButtonSrText
}) {
  const textFieldRef = useRef(null);

  const onClear = useCallback(() => {
    handleClear();
    if (textFieldRef.current) {
      textFieldRef.current.getWrappedInstance().focus();
    }
  }, [handleClear]);

  const renderClearButtonScreenReaderText = () => {
    if (clearButtonSrText) {
      return <ScreenReaderMessage>{clearButtonSrText}</ScreenReaderMessage>;
    }

    return (
      <FormattedMessage id="ada_label_clear_text">
        {message => <ScreenReaderMessage>{message}</ScreenReaderMessage>}
      </FormattedMessage>
    );
  };

  const renderClearButton = () => {
    if (!hideClearButton) {
      return (
        <TextButton
          className="clear-icon"
          handleClick={onClear}
          type="button"
          dataKey="clearable-text-field-clear-button"
        >
          <CrossIcon />
          {renderClearButtonScreenReaderText()}
        </TextButton>
      );
    }

    return null;
  };

  return (
    <div className="cp-clearable-text-field">
      <AccessibleTextField
        id={id}
        value={value}
        type={type}
        labelledById={labelledById}
        describedById={describedById}
        fieldErrors={fieldErrors}
        errorKey={errorKey}
        handleChange={handleChange}
        placeholder={placeholder}
        maxLength={maxLength}
        focus={focus}
        testId={inputTestId}
        info={info}
        inputIcon={renderClearButton()}
        className={className}
        characterCountSrOnly={characterCountSrOnly}
        ref={textFieldRef}
      />
    </div>
  );
}

ClearableTextField.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  hideClearButton: PropTypes.bool,
  className: PropTypes.string,
  describedById: PropTypes.string,
  fieldErrors: PropTypes.objectOf(fieldErrorsShape),
  errorKey: PropTypes.string,
  labelledById: PropTypes.string.isRequired,
  focus: PropTypes.bool,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  type: PropTypes.string,
  inputTestId: PropTypes.string.isRequired,
  characterCountSrOnly: PropTypes.bool,
  info: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  handleChange: PropTypes.func.isRequired,
  handleClear: PropTypes.func.isRequired,
  clearButtonSrText: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
};

ClearableTextField.defaultProps = {
  hideClearButton: false
};

export default ClearableTextField;
